import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import styles from "./Order.module.css";
import Api from "../../utils/Api/Api";
import { money } from "../../utils/format";
import { track, trackPurchase, EVENTS } from "../../utils/analyticsService";
import { TopBar } from "../../components/TopBar/TopBar";
import { LicenceKeyCard } from "../../components/LicenceKeyCard/LicenceKeyCard";
import { ErrorFallback } from "../../components/Error/Error";
import { GiftReady } from "../GiftReady/GiftReady";
import { isGiftOrderRow, isRememberedGiftOrder } from "../../utils/gift";
import coinLogo from "../../asset/icons/fleek-coin-logo.png";
import FleekIcon from "../../asset/icons/Brand_logo_Sq.png";
import { isIndia } from "../../utils/market";

const FAILURE_REDIRECT_MS = 5000;

/**
 * Order placed, rebuilt from design/Purchase Flow.dc.html (isSuccess block).
 */
export const Order = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get("order_id");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  // A gift order ends on the screen that sends it, not on this receipt: the
  // code belongs to whoever claims the link, and the server withholds it here.
  const [isGift, setIsGift] = useState(false);
  // process_payment's answer: what was charged, and in which currency. The
  // order row carries no currency of its own, so without this a dollar order
  // read as rupees ("₹1" for $1.28).
  const [paid, setPaid] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const viaV4 = async () => {
      const { data: payment } = await Api.ProcessPaymentV4({ order_id: orderId });
      if (!payment?.order_id) throw new Error("V4 returned no order");
      const { data: rows } = await Api.GetOrderDetailsFromPaymentId({ payment_order_id: payment.payment_id });
      if (!rows?.length) throw new Error("V4 returned no order details");
      // A gift is always an order of its own; if this payment covered more
      // than one, the gift's row is the one this screen is about.
      const row = rows.find((r) => isGiftOrderRow(r) || isRememberedGiftOrder(r?.id)) || rows[0];
      return { row, payment, settled: payment.status === "paid" && payment.is_purchase_done };
    };

    (async () => {
      try {
        // Juspay (the v3 fallback) is retired; every order pays through v4.
        const result = await viaV4();
        if (cancelled) return;

        if (!result.settled) {
          setTimeout(() => navigate("/checkout?status=failed"), FAILURE_REDIRECT_MS);
          setLoading(false);
          return;
        }

        setOrder(result.row);
        setPaid(result.payment);
        // Checkout remembers the placed order's UUID; older links may carry it
        // in the URL instead of the short reference, so check both.
        setIsGift(
          isGiftOrderRow(result.row) || isRememberedGiftOrder(result.row?.id) || isRememberedGiftOrder(orderId)
        );
        setLoading(false);
        track(EVENTS.ORDER_VIEWED, { order_id: orderId, context: "order_placed" });
        // GA4 ecommerce purchase, once per order id (deduped in the wrapper).
        trackPurchase(result.row);
      } catch (err) {
        if (cancelled) return;
        console.error("Order placed failed to load:", err);
        setHasError(true);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const view = useMemo(() => {
    if (!order) return null;
    const item = order.new_ordered_items?.[0] || order;
    const plan = item?.subscription_plan || {};
    const bundle = order.bundle;
    const key = item?.coupon_code?.coupon_code || null;
    return {
      key,
      link: item?.coupon_code?.coupon_link || plan.code_redemption_url || null,
      isSample: !!order.is_mock,
      pending: !key,
      reference: item?.representation_id || order.representation_id || orderId,
      planId: plan.subscription_plan_id ?? null,
      logo: plan.image_url || bundle?.image_url || FleekIcon,
      brand: bundle?.name || plan.service_name || item?.product_name || "Your order",
      plan: bundle ? "Bundle" : plan.plan_name || "",
      amount: paid?.amount ?? order.payment?.amount,
      currency: paid?.currency || order.payment?.currency_type || order.details?.currency_type,
      coinsEarned: order.coins_earned ?? order.coins_credited ?? item?.coins_earned ?? null,
    };
  }, [order, orderId, paid]);

  if (hasError) return <ErrorFallback />;
  if (isGift && order) {
    // The gift API is keyed by the placed order's UUID (the row's `id`), not
    // the short payment reference in ?order_id= (PaymentV2's
    // order_representation_id), which /gifts/v1/order/<uuid> 404s on.
    return <GiftReady orderId={order.id || orderId} orderItemId={order.new_ordered_items?.[0]?.id ?? null} />;
  }

  return (
    <div className={styles.page}>
      <TopBar title="Order placed" backTo="/marketplace" />
      <div className={styles.shell}>
        {loading || !view ? (
          <div className={styles.skeleton} />
        ) : (
          <>
            <div className={styles.head}>
              <span className={`${styles.icon} ${view.pending ? styles.iconPending : styles.iconDone}`} aria-hidden="true">
                {view.pending ? (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                  </svg>
                ) : (
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </span>
              <p className={styles.title}>{view.pending ? "Payment received" : "You're all set"}</p>
              <p className={styles.body}>
                {!isIndia()
                  ? view.pending
                    ? "Your order is confirmed. Your plan details arrive by email within 24 hours."
                    : "Your plan details are ready below, and we've emailed them to you."
                  : view.pending
                    ? "Your order is confirmed. This plan is activated by hand, so the key follows shortly."
                    : "Your licence key is ready below, and we've emailed and WhatsApped it to you."}
              </p>
            </div>

            <div className={styles.keyCard}>
              <LicenceKeyCard
                label="Your licence key"
                licenceKey={view.key}
                activationUrl={view.link}
                tracking={{ order_id: orderId, plan_id: view.planId, brand: view.brand }}
              />
            </div>
            {view.isSample && (
              <p className={styles.sampleNote}>Sample key from the design — this is a mock order, not a real licence.</p>
            )}

            <section className={styles.orderCard}>
              <div className={styles.orderHead}>
                <span className={styles.orderLabel}>Order</span>
                <span className={styles.orderId}>#{view.reference}</span>
              </div>
              <div className={styles.item}>
                <img className={styles.itemLogo} src={view.logo} alt="" onError={(e) => { e.currentTarget.src = FleekIcon; }} />
                <div className={styles.itemText}>
                  <div className={styles.itemBrand}>{view.brand}</div>
                  {view.plan && <div className={styles.itemPlan}>{view.plan}</div>}
                </div>
                {view.amount != null && <span className={styles.itemPrice}>{money(view.amount, view.currency)}</span>}
              </div>
              {view.coinsEarned != null && (
                <div className={styles.coins}>
                  <img src={coinLogo} alt="" className={styles.coinsIcon} />
                  <span className={styles.coinsText}>
                    {Number(view.coinsEarned).toLocaleString("en-IN")} coins added to your wallet
                  </span>
                </div>
              )}
            </section>

            <div className={styles.actions}>
              <button type="button" className={`${styles.cta} ${styles.ctaPrimary}`} onClick={() => navigate("/myorder")}>
                View my orders
              </button>
              <button type="button" className={`${styles.cta} ${styles.ctaGhost}`} onClick={() => navigate("/marketplace")}>
                Keep shopping
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Order;
